import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Logo } from "./Logo";
import { TeamBadge } from "./TeamBadge";
import { NotificationBell } from "./NotificationBell";
import { LanguageSelector } from "./LanguageSelector";
import { useSport } from "../context/SportContext";
import api from "../lib/api";
import { MagnifyingGlass, X, SoccerBall, TennisBall, Basketball, Bookmarks } from "@phosphor-icons/react";

const SPORT_ICON = { foot: SoccerBall, tennis: TennisBall, basket: Basketball };
const SPORT_BALL = { foot: "/ball-soccer-192.png", tennis: "/ball-tennis-192.png", basket: "/ball-basket-192.png" };

export const Header = ({ showSearch = true }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { sport, setSport, SPORTS } = useSport();
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [open, setOpen] = useState(false);
  const [recent, setRecent] = useState([]);
  const [recentQ, setRecentQ] = useState([]);
  const timer = useRef(null);
  const boxRef = useRef(null);

  const loadRecent = () => {
    try { setRecent(JSON.parse(localStorage.getItem("hitmax_recent_matches") || "[]")); }
    catch { setRecent([]); }
    try { setRecentQ(JSON.parse(localStorage.getItem("hitmax_recent_queries") || "[]")); }
    catch { setRecentQ([]); }
  };

  const saveQuery = (query) => {
    const val = (query || "").trim();
    if (val.length < 2) return;
    try {
      const cur = JSON.parse(localStorage.getItem("hitmax_recent_queries") || "[]");
      const next = [val, ...cur.filter((x) => x.toLowerCase() !== val.toLowerCase())].slice(0, 6);
      localStorage.setItem("hitmax_recent_queries", JSON.stringify(next));
      setRecentQ(next);
    } catch { /* ignore */ }
  };

  const hasResults = (r) => !!(r && (r.matches?.length || r.teams?.length || r.players?.length || r.competitions?.length));

  const pick = (path) => { saveQuery(q); navigate(path); setOpen(false); setQ(""); };

  useEffect(() => {
    if (!q.trim()) { setRes(null); return; }
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      const { data } = await api.get(`/search?q=${encodeURIComponent(q)}`);
      setRes(data);
    }, 250);
  }, [q]);

  // Ferme le panneau au clic extérieur et mémorise la recherche en cours
  // (même sans avoir cliqué un résultat) pour la retrouver dans les récentes.
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) {
        if (q.trim() && hasResults(res)) saveQuery(q);
        setOpen(false); setQ(""); setRes(null);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open, q, res]);

  return (
    <header className="sticky top-0 z-40 glass border-b border-white/10 px-5 pt-4 pb-3">
      <div className="flex items-center justify-between gap-2">
        <Logo size="h-9 shrink-0" />
        <div className="flex items-center gap-1 shrink-0">
          <div className="flex items-center gap-1">
            <NotificationBell />
            <button
              data-testid="followed-matches-btn"
              onClick={() => navigate("/mes-matchs")}
              aria-label="Mes matchs suivis"
              title="Mes matchs suivis"
              className="w-8 h-8 rounded-full flex items-center justify-center text-white/60 hover:text-perf-excellent transition-colors"
            >
              <Bookmarks size={18} weight="regular" />
            </button>
          </div>
          <span className="w-px h-5 bg-white/12 mx-1" aria-hidden="true" />
          <LanguageSelector variant="header" />
        </div>
      </div>

      {/* Sélecteur de sport réparti sur toute la largeur (espacement égal) */}
      <div className="mt-3 grid grid-cols-3 gap-2" data-testid="sport-selector">
        {SPORTS.map((s) => {
          const on = sport === s.key;
          return (
            <button
              key={s.key}
              data-testid={`sport-${s.key}`}
              onClick={() => setSport(s.key)}
              aria-label={s.label}
              className="flex items-center justify-center gap-2 px-3 py-1 rounded-full whitespace-nowrap w-full transition-all duration-200 border"
              style={on
                ? { borderColor: s.color, background: `${s.color}1A`, boxShadow: `0 0 10px ${s.color}44` }
                : { borderColor: "rgba(255,255,255,0.08)", opacity: 0.55 }}
            >
              <img
                src={`${process.env.PUBLIC_URL || ""}${SPORT_BALL[s.key]}`}
                alt={s.label}
                className="w-6 h-6 object-contain"
                draggable="false"
              />
              <span className="text-[12px] font-bold" style={on ? { color: s.color } : { color: "rgba(255,255,255,0.7)" }}>{s.label}</span>
            </button>
          );
        })}
      </div>

      {showSearch && (
        <div className="mt-3 relative" ref={boxRef}>
          <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-xl px-3 h-11">
            <MagnifyingGlass size={18} color="rgba(255,255,255,0.4)" />
            <input
              data-testid="search-input"
              value={q}
              onFocus={() => { setOpen(true); loadRecent(); }}
              onChange={(e) => { setQ(e.target.value); setOpen(true); }}
              onKeyDown={(e) => { if (e.key === "Enter" && q.trim()) saveQuery(q); }}
              placeholder={t("header.search_placeholder")}
              className="flex-1 bg-transparent outline-none text-sm text-white placeholder:text-white/30"
            />
            {q && <button type="button" aria-label={t("header.clear_search", "Effacer la recherche")} onClick={() => { setQ(""); setRes(null); loadRecent(); }}><X size={16} color="rgba(255,255,255,0.4)" aria-hidden="true" /></button>}
          </div>

          {open && !q.trim() && (recentQ.length > 0 || recent.length > 0) && (
            <div className="absolute inset-x-0 mt-2 hm-card p-2 z-50 max-h-80 overflow-auto no-scrollbar" data-testid="search-recent">
              {recentQ.length > 0 && (
                <>
                  <div className="flex items-center justify-between px-2 py-1">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-white/40">{t("header.recent_searches")}</p>
                    <button data-testid="clear-recent-searches"
                      onClick={() => { localStorage.removeItem("hitmax_recent_queries"); setRecentQ([]); }}
                      className="text-[10px] text-white/40 hover:text-white/70">{t("header.clear")}</button>
                  </div>
                  {recentQ.map((rq) => (
                    <button key={rq} data-testid={`recent-query-${rq}`}
                      onClick={() => { setQ(rq); setOpen(true); }}
                      className="w-full flex items-center gap-2 p-2 rounded-lg hover:bg-white/5 text-left">
                      <MagnifyingGlass size={15} color="rgba(255,255,255,0.4)" />
                      <span className="text-sm text-white/85 truncate">{rq}</span>
                    </button>
                  ))}
                </>
              )}
              {recent.length > 0 && (
                <>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-white/40 px-2 py-1 mt-1">{t("header.recent")}</p>
                  {recent.map((m) => (
                    <button key={m.match_id} data-testid={`recent-match-${m.match_id}`}
                      onClick={() => { navigate(`/match/${m.match_id}`); setOpen(false); }}
                      className="w-full flex items-center gap-2 p-2 rounded-lg hover:bg-white/5">
                      <TeamBadge team={m.home} size={24} />
                      <span className="text-sm font-medium truncate">{m.home?.short || m.home?.name}</span>
                      <span className="text-xs text-white/30">vs</span>
                      <span className="text-sm font-medium truncate">{m.away?.short || m.away?.name}</span>
                      <TeamBadge team={m.away} size={24} />
                      <span className="ml-auto text-[10px] text-white/40 uppercase shrink-0">{m.competition}</span>
                    </button>
                  ))}
                </>
              )}
            </div>
          )}

          {open && res && (q.trim().length > 0) && (
            <div className="absolute inset-x-0 mt-2 hm-card p-2 z-50 max-h-80 overflow-auto no-scrollbar" data-testid="search-results">
              {res.matches?.map((m) => (
                <button key={m.match_id} data-testid={`search-match-${m.match_id}`}
                  onClick={() => pick(m.source === "live" ? `/live/${m.match_id}` : `/match/${m.match_id}`)}
                  className="w-full flex items-center gap-2 p-2 rounded-lg hover:bg-white/5">
                  <TeamBadge team={m.home} size={24} />
                  <span className="text-sm font-medium truncate">{m.home?.short || m.home?.name}</span>
                  <span className="text-xs text-white/30">vs</span>
                  <span className="text-sm font-medium truncate">{m.away?.short || m.away?.name}</span>
                  <TeamBadge team={m.away} size={24} />
                  <span className="ml-auto text-[10px] text-white/40 uppercase shrink-0">{m.is_live ? "🔴 LIVE" : t("header.match")}</span>
                </button>
              ))}
              {res.teams?.map((tm) => (
                <button key={tm.team_id} onClick={() => pick(`/club/${tm.team_id}`)}
                  className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-white/5">
                  <TeamBadge team={tm} size={30} />
                  <span className="text-sm font-medium">{tm.name}</span>
                  <span className="ml-auto text-[10px] text-white/40 uppercase">{t("header.club")}</span>
                </button>
              ))}
              {res.players?.map((p) => (
                <button key={p.player_id} onClick={() => pick(`/player/${p.player_id}`)}
                  className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-white/5">
                  <div className="w-[30px] h-[30px] rounded-full bg-white/10 flex items-center justify-center text-xs font-bold">{p.club}</div>
                  <span className="text-sm font-medium">{p.name}</span>
                  <span className="ml-auto text-[10px] text-white/40 uppercase">{t("header.player")}</span>
                </button>
              ))}
              {res.competitions?.map((c) => (
                <button key={c.comp_id} onClick={() => pick(`/stats?comp=${c.comp_id}`)}
                  className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-white/5">
                  <span className="text-sm font-medium">{c.name}</span>
                  <span className="ml-auto text-[10px] text-white/40 uppercase">{t("header.competition")}</span>
                </button>
              ))}
              {!res.teams?.length && !res.players?.length && !res.competitions?.length && !res.matches?.length && (
                <p className="text-sm text-white/40 p-3 text-center">{t("header.no_results")}</p>
              )}
            </div>
          )}
        </div>
      )}
    </header>
  );
};
